import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Circle, ImageSquare, UsersThree, Receipt, X, Broom } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";

const STEPS = {
  logo: { label: "Add your company logo", hint: "Shown on invoices, orders and emails", to: "/company-profile", icon: ImageSquare },
  invite: { label: "Invite a teammate", hint: "Give your team their own sign-in", to: "/team", icon: UsersThree },
  order: { label: "Create your first sales order", hint: "Start selling to a real customer", to: "/sales/doc/new?type=Order&tab=orders", icon: Receipt },
};

export const useOnboarding = () => useQuery({ queryKey: ["onboarding"], queryFn: async () => (await api.get("/onboarding")).data, staleTime: 0 });

export const ClearSampleDataButton = ({ count, className = "" }) => {
  const qc = useQueryClient();
  const clear = useMutation({
    mutationFn: async () => (await api.post("/onboarding/clear-sample")).data,
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success("Sample data cleared", { description: r.kept.length ? `Kept because they're in use: ${r.kept.join(", ")}` : undefined });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  if (!count) return null;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className={`rounded-sm h-9 ${className}`} data-testid="clear-sample-data-button">
          <Broom size={16} weight="bold" className="mr-1.5" /> Clear sample data
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-sm" data-testid="clear-sample-data-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Clear sample data?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the sample customers, vendors, items, orders, invoice and purchase orders ({count} records). Anything you created yourself stays, and sample records you're already using are kept. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-sm" data-testid="clear-sample-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => clear.mutate()} className="rounded-sm" data-testid="clear-sample-confirm">Clear sample data</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const SampleDataButton = () => {
  const { data } = useOnboarding();
  return <ClearSampleDataButton count={data?.sample_count} />;
};

const StepRow = ({ step }) => {
  const navigate = useNavigate();
  const s = STEPS[step.key];
  const Icon = s.icon;
  return (
    <button onClick={() => navigate(s.to)} data-testid={`onboarding-step-${step.key}`} data-done={step.done}
      className="group bg-card px-4 py-3 flex items-center gap-3 text-left hover:bg-muted/50 transition-colors">
      {step.done ? <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0" /> : <Circle size={22} className="text-muted-foreground shrink-0" />}
      <Icon size={18} className="text-primary shrink-0" />
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${step.done ? "line-through text-muted-foreground" : ""}`}>{s.label}</div>
        <div className="text-xs text-muted-foreground">{s.hint}</div>
      </div>
    </button>
  );
};

export const GettingStartedCard = () => {
  const qc = useQueryClient();
  const { data } = useOnboarding();
  const dismiss = useMutation({
    mutationFn: async () => (await api.post("/onboarding/dismiss")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["onboarding"] }),
  });
  if (!data?.show_checklist) return null;
  const done = data.steps.filter((s) => s.done).length;
  return (
    <div className="border border-border bg-card" data-testid="getting-started-card">
      <div className="px-5 py-3 border-b border-border flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold">Get started</h3>
          <p className="text-xs text-muted-foreground" data-testid="onboarding-progress">{done} of {data.steps.length} done</p>
        </div>
        <ClearSampleDataButton count={data.sample_count} className="px-2.5 sm:px-4" />
        <button onClick={() => dismiss.mutate()} className="p-1.5 text-muted-foreground hover:text-foreground" aria-label="Dismiss" data-testid="onboarding-dismiss">
          <X size={16} weight="bold" />
        </button>
      </div>
      <div className="grid sm:grid-cols-3 gap-px bg-border">
        {data.steps.map((s) => <StepRow key={s.key} step={s} />)}
      </div>
    </div>
  );
};
